import { useCallback, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import { AllocationName, MarketAdjustmentWritable } from 'api';
import { ValuationWithReferences } from 'pages/ValuationsAllocation/types';
import { useCreateNewVersion } from 'services/hooks/valuations';
import { dbShortDate, getArrayValue, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import {
  UseFundInvestmentAllocationVersionParams,
  UseFundInvestmentAllocationVersionReturn,
} from './useFundInvestmentAllocationVersion.types';

const useFundInvestmentAllocationVersion = (
  params: UseFundInvestmentAllocationVersionParams
): UseFundInvestmentAllocationVersionReturn => {
  const {
    allocationVersionDialogProps,
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocation,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    currentMeasurementDate,
    currentMeasurementDateId,
    marketCurrency,
    marketDate,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    valuationByAllocation,
  } = params;
  // Allocation version dialog
  const {
    getVersions: getAllocationVersions,
    onClose: onCloseAllocationVersionDialog,
    open: isAllocationVersionDialogOpen,
    versions: allocationVersions,
  } = getObjectValue(allocationVersionDialogProps);

  // Create New Version
  const { mutateAsync: saveNewAllocationVersion } = useCreateNewVersion();

  // New allocation version
  const [isOpenNewAllocationVersionDialog, setIsOpenNewAllocationVersionDialog] = useState<boolean>(false);

  const showNewAllocationVersionDialog = useCallback(() => setIsOpenNewAllocationVersionDialog(true), []);

  const closeNewAllocationVersionDialog = useCallback(() => {
    setIsOpenNewAllocationVersionDialog(false);
  }, []);

  // Create New Valuations Allocation Version
  const createNewAllocationVersion = useCallback(async (saveParams: { keep_notes: boolean; name: string }) => {
    const { keep_notes: keepNotes, name } = saveParams;

    if (!isEmpty(valuationByAllocation) && !isEmpty(currentAllocation)) {
      const hasInvalidCells = !isNil(areAllCellsValid) && !areAllCellsValid();

      setHasValidationErrors(hasInvalidCells);

      // If there are invalid cells or funds, do not proceed with saving
      if (hasInvalidCells) {
        setIsValidating(false);
        return;
      }

      const marketAdjustmentIsNotAvailable =
        isNil(currentAsOfDate) && isNil(currentMarketComparisons) && isNil(currentMarketAdjustment);

      let updatedMarketAdjustments: MarketAdjustmentWritable[] = isEmpty(currentDeletedMarketAdjustments)
        ? [
          {
            ...(currentMarketAdjustment as MarketAdjustmentWritable),
            as_of_date: getStringValue(currentAsOfDate),
            market_comparisons: getArrayValue(currentMarketComparisons),
            deleted_market_comparisons: getArrayValue(currentDeletedMarketComparisons),
          },
        ]
        : []; // If there are deleted market adjustments, do not include the current market adjustment

      if (marketAdjustmentIsNotAvailable) {
        updatedMarketAdjustments = getArrayValue(valuationByAllocation.market_adjustments);
      }

      const valuationToBeDuplicated = {
        ...valuationByAllocation,
        name,
        allocation: {
          ...currentAllocation,
          version: getNumberValue(allocationVersions?.length), // Number is increased by 1 in the backend
          is_final: false,
          name,
        },
        deleted_approaches: [],
        deleted_market_adjustments: getArrayValue(currentDeletedMarketAdjustments),
        market_adjustments: updatedMarketAdjustments,
        market_currency: marketCurrency,
        market_date: dbShortDate(marketDate),
      } as ValuationWithReferences;

      // Explicitly handle keep_notes: ensure it's a boolean, default to true if undefined
      const keepNotesValue = keepNotes ?? true;
      
      const { valuation: newValuationVersion } = await saveNewAllocationVersion({
        valuation: valuationToBeDuplicated,
        keep_notes: keepNotesValue,
      });
      const { allocation: newAllocationVersion } = getObjectValue(newValuationVersion);

      if (!isEmpty(newAllocationVersion)) {
        const { id: newValuationId } = getObjectValue(newValuationVersion);

        const {
          id: newAllocationId,
          is_final: isFinal,
          is_published: isPublished,
          name: newAllocationName,
          slug,
          version,
        } = getObjectValue(newAllocationVersion);

        const createdAllocationVersion = {
          id: newAllocationId,
          is_final: isFinal,
          is_published: isPublished,
          name: newAllocationName,
          slug,
          valuation: newValuationId,
          version,
        } as AllocationName;

        getAllocationVersions?.(createdAllocationVersion);
      }

      // Mark changes as not pending
      cleanUnsavedChanges();
      setArePendingChanges(false);
    }
  }, [
    allocationVersions,
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocation,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    getAllocationVersions,
    marketCurrency,
    marketDate,
    saveNewAllocationVersion,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    valuationByAllocation,
  ]);

  const createNewBlankAllocationVersion = useCallback(async () => {
    if (!isEmpty(valuationByAllocation) && !isEmpty(currentAllocation)) {
      const hasInvalidCells = !isNil(areAllCellsValid) && !areAllCellsValid();

      setHasValidationErrors(hasInvalidCells);

      // If there are invalid cells or funds, do not proceed with saving
      if (hasInvalidCells) {
        setIsValidating(false);
        return;
      }

      // Version number and ownership will be set in by the backend API
      const blankValuationAllocation = {
        allocation_id: currentAllocation.id,
        allocation: {
          allocation_scenarios: [],
          company_measurement_date: currentMeasurementDateId,
          date: currentMeasurementDate,
          fund_conclusion: [],
          id: currentAllocation.id,
          is_final: false,
          is_published: false,
          name: `Firm version - ${currentMeasurementDate}`,
        },
        deleted_approaches: [],
        deleted_market_adjustments: [],
        id: valuationByAllocation.id,
        market_adjustments: [],
        market_currency: marketCurrency,
        name: `Firm version - ${currentMeasurementDate}`,
        valuations_approaches: [],
        weighting_probabilities: [],
      } as unknown as ValuationWithReferences;

      const { valuation: blankValuationVersion } = await saveNewAllocationVersion({
        valuation: blankValuationAllocation,
      });
      const { allocation: blankAllocationVersion } = getObjectValue(blankValuationVersion);

      if (!isEmpty(blankAllocationVersion)) {
        const { id: blankValuationVersionId } = getObjectValue(blankValuationVersion);

        const {
          id: blankAllocationVersionId,
          is_final: isFinal,
          is_published: isPublished,
          name,
          slug,
          version,
        } = getObjectValue(blankAllocationVersion);

        const createdAllocationVersion = {
          id: blankAllocationVersionId,
          is_final: isFinal,
          is_published: isPublished,
          name,
          slug,
          valuation: blankValuationVersionId,
          version,
        } as AllocationName;

        getAllocationVersions?.(createdAllocationVersion);
      }

      // Mark changes as not pending
      cleanUnsavedChanges();
      setArePendingChanges(false);
    }
  }, [
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocation,
    currentMeasurementDate,
    currentMeasurementDateId,
    getAllocationVersions,
    marketCurrency,
    saveNewAllocationVersion,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    valuationByAllocation,
  ]);

  return {
    allocationVersions,
    closeNewAllocationVersionDialog,
    createNewAllocationVersion,
    createNewBlankAllocationVersion,
    getAllocationVersions,
    isAllocationVersionDialogOpen,
    isOpenNewAllocationVersionDialog,
    onCloseAllocationVersionDialog,
    showNewAllocationVersionDialog,
  };
};

export default useFundInvestmentAllocationVersion;
